import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Alert, Spinner, Badge } from 'react-bootstrap';
import { useLazyGetPlannerSummaryQuery } from '../app/api';
import { graphTasksRequest } from '../msalConfig';
import useGraphToken from './useGraphToken';
import { useIsMobile } from './utils';

// Planner summary (beta).
//
// Every basic Planner plan the signed-in employee belongs to, with the numbers
// computed server-side and an AI read over them. Delegated Tasks.Read token
// acquired here and passed per request; the hub never holds it.
//
// The one thing to understand before changing any copy on this page: Planner's
// API exposes createdDateTime and completedDateTime and NO modified timestamp.
// So "activity" here means created-or-completed, and nothing else — a task that
// was renamed, reassigned, moved between buckets or given a new due date looks
// identical to one nobody has touched. Every label that mentions activity is
// worded to that limit on purpose. Don't relax the wording without first
// changing where the data comes from (see integrations/planner_metrics.py).

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
  amber:     '#b7791f',
};

const PATH = '/planner';

const cardStyle = {
  background: 'white',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
  overflow: 'hidden',
};

// Long plan and task titles are the norm on a shared board. Without this, one of
// them forces horizontal scroll on the whole page.
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

// Plan-level attention. Colour is deliberately redundant here — every badge
// carries its own word, so the state is never conveyed by hue alone. The
// palette validator fails red/amber/green as a *categorical* set (red↔amber sit
// at ΔE 14.1 for normal vision), which is exactly why colour is not allowed to
// be the identifier anywhere on this page.
const ATTENTION = {
  urgent: { label: 'Needs attention', color: brand.red,   icon: 'exclamation-triangle-fill' },
  watch:  { label: 'Worth watching', color: brand.amber, icon: 'eye-fill' },
  ok:     { label: 'Moving',         color: brand.green, icon: 'check-circle-fill' },
};

function dueLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function btn(bg, fg) {
  return {
    background: bg, color: fg, border: 'none', borderRadius: 8,
    padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.85rem',
  };
}

/** One KPI number. Icon + label always present, so the tint is decoration. */
function Tile({ icon, label, value, tint, hint }) {
  return (
    <div style={{ ...cardStyle, padding: '0.85rem 1rem', flex: '1 1 140px', minWidth: 140 }} title={hint || ''}>
      <div className="d-flex align-items-center gap-2" style={{ color: brand.charcoal, fontSize: '0.75rem' }}>
        <i className={`bi bi-${icon}`} style={{ color: tint || brand.charcoal }} />
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{label}</span>
      </div>
      <div style={{ color: tint || brand.deepBlue, fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2 }}>
        {value}
      </div>
    </div>
  );
}
Tile.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tint: PropTypes.string,
  hint: PropTypes.string,
};

/** Open work per bucket: one measure, one hue, direct-labelled. */
function BucketBars({ buckets }) {
  const max = useMemo(
    () => buckets.reduce((m, b) => Math.max(m, b.open), 0), [buckets],
  );
  if (!buckets.length || !max) return null;
  return (
    <div className="d-flex flex-column" style={{ gap: 2 }}>
      {buckets.slice(0, 8).map((b) => (
        <div key={b.name} className="d-flex align-items-center gap-2" title={`${b.name}: ${b.open} open`}>
          <div style={{ ...WRAP, flex: '0 0 30%', fontSize: '0.76rem', color: brand.charcoal }}>{b.name}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: `${Math.max(4, (b.open / max) * 100)}%`,
                height: 10,
                background: brand.tealBlue,
                // Rounded only on the data end; the baseline end stays square so
                // every bar reads from the same origin.
                borderRadius: '0 4px 4px 0',
              }}
            />
          </div>
          <div style={{ flex: '0 0 2rem', textAlign: 'right', fontSize: '0.76rem', fontWeight: 700, color: brand.deepBlue }}>
            {b.open}
          </div>
        </div>
      ))}
    </div>
  );
}
BucketBars.propTypes = {
  buckets: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string, open: PropTypes.number,
  })).isRequired,
};

/** A short list of real task titles behind one of the numbers. */
function TaskList({ title, rows, tint }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mt-2">
      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: tint, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </div>
      <ul className="mb-0 ps-3" style={{ fontSize: '0.82rem' }}>
        {rows.map((r, i) => (
          <li key={`${r.title}-${i}`} style={WRAP}>
            {r.web_url ? (
              // Shares one named tab across the page, so working down a plan
              // doesn't leave a dozen Planner tabs behind. rel is omitted
              // deliberately: noopener/noreferrer make the browser ignore the
              // target name and open a fresh tab anyway.
              <a href={r.web_url} target="pcaPlanner" style={{ color: brand.deepBlue }}>{r.title}</a>
            ) : r.title}
            {r.due && <span style={{ color: brand.charcoal }}> · due {dueLabel(r.due)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
TaskList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  tint: PropTypes.string,
};

function PlanCard({ plan, note, activityDays }) {
  const c = plan.counts || {};
  const s = plan.samples || {};
  const att = ATTENTION[note?.attention] || null;
  return (
    <div style={{ ...cardStyle, padding: '1rem' }}>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <h6 className="mb-0" style={{ ...WRAP, color: brand.deepBlue, fontWeight: 800, flex: '1 1 auto' }}>
          {plan.web_url ? (
            <a href={plan.web_url} target="pcaPlanner" style={{ color: brand.deepBlue, textDecoration: 'none' }}>
              {plan.title}
            </a>
          ) : plan.title}
        </h6>
        {plan.mine_only && (
          // Not decoration: every number on this card covers only the reader's
          // own tasks, so the card must say that wherever the numbers are read.
          <Badge bg="" style={{ background: brand.charcoal, fontSize: '0.68rem' }}
            title="We can read your tasks on this board but not the rest of it, so these figures cover your tasks only.">
            <i className="bi bi-person-fill me-1" />Your tasks only
          </Badge>
        )}
        {att && (
          <Badge bg="" style={{ background: att.color, fontSize: '0.68rem' }}>
            <i className={`bi bi-${att.icon} me-1`} />{att.label}
          </Badge>
        )}
      </div>

      {note?.summary && (
        <p className="mb-2 mt-2" style={{ ...WRAP, fontSize: '0.88rem', color: brand.charcoal }}>{note.summary}</p>
      )}

      <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: '0.78rem', color: brand.charcoal }}>
        <span><strong style={{ color: brand.deepBlue }}>{c.open || 0}</strong> open</span>
        {!!c.overdue && <span style={{ color: brand.red }}><strong>{c.overdue}</strong> overdue</span>}
        {!!c.due_soon && <span style={{ color: brand.amber }}><strong>{c.due_soon}</strong> due this week</span>}
        {!!c.unassigned && <span><strong>{c.unassigned}</strong> unassigned</span>}
        {!!c.stale && <span><strong>{c.stale}</strong> never started</span>}
        <span>
          <strong style={{ color: brand.green }}>{c.completed_recent || 0}</strong> completed
          {' '}/ <strong>{c.created_recent || 0}</strong> created in {activityDays}d
        </span>
      </div>

      {plan.truncated && (
        <div className="mt-2" style={{ fontSize: '0.74rem', color: brand.amber }}>
          <i className="bi bi-exclamation-triangle me-1" />
          This plan has more tasks than were read — the figures above are a floor, not a total.
        </div>
      )}

      {!!(plan.buckets || []).length && (
        <div className="mt-3">
          <div style={{ fontSize: '0.72rem', fontWeight: 800, color: brand.charcoal, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Open work by bucket
          </div>
          <div className="mt-1"><BucketBars buckets={plan.buckets} /></div>
        </div>
      )}

      <TaskList title="Overdue" rows={s.overdue} tint={brand.red} />
      <TaskList title="Due this week" rows={s.due_soon} tint={brand.amber} />
      <TaskList title="Never started" rows={s.stale} tint={brand.charcoal} />
    </div>
  );
}
PlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  note: PropTypes.object,
  activityDays: PropTypes.number.isRequired,
};

export default function Planner() {
  const isMobile = useIsMobile();
  const [load, res] = useLazyGetPlannerSummaryQuery();

  const onToken = useCallback((accessToken, opts) => {
    load({ graphToken: accessToken }, opts?.preferCache !== false);
  }, [load]);

  const { phase, authError, connecting, needsReload, connect, acquire, hubEmail } =
    useGraphToken(graphTasksRequest, PATH, onToken);

  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // preferCache false — an explicit refresh is the one case where re-reading
      // every plan (and re-billing the model) is what was asked for.
      await acquire({ preferCache: false });
    } finally {
      setRefreshing(false);
    }
  }, [acquire]);

  const data = res.data;
  const totals = data?.totals || {};
  const activityDays = data?.activity_days || 7;
  // plan_id → note, so a plan the model said nothing about still renders.
  const notes = useMemo(() => {
    const m = {};
    for (const n of data?.summary?.plan_notes || []) m[n.plan_id] = n;
    return m;
  }, [data]);

  let body;
  if (phase === 'connect') {
    body = (
      <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center' }}>
        <i className="bi bi-kanban-fill" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>Connect Planner</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 480, margin: '0 auto 1rem' }}>
          This page reads the Planner boards you already belong to — read-only, and nothing is
          written back to Planner. Connecting {hubEmail}.
        </p>
        {needsReload ? (
          <button type="button" onClick={() => window.location.reload()} style={btn(brand.deepBlue, 'white')}>
            <i className="bi bi-arrow-clockwise me-1" />Reload and try again
          </button>
        ) : (
          <button type="button" onClick={() => connect(isMobile)} disabled={connecting}
            style={btn(connecting ? brand.softGray : brand.goldenrod, brand.deepBlue)}>
            {connecting ? 'Waiting for Microsoft…' : 'Connect Planner'}
          </button>
        )}
        {authError && <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>{authError}</Alert>}
      </div>
    );
  } else if (res.isError) {
    body = (
      <Alert variant="warning" style={{ fontSize: '0.9rem' }}>
        {res.error?.data?.detail || 'Your plans could not be read just now. Please try again shortly.'}
      </Alert>
    );
  } else if (res.isFetching || !data) {
    body = (
      <div className="text-center py-5" style={{ color: brand.charcoal }}>
        <Spinner animation="border" size="sm" className="me-2" />
        Reading your Planner boards…
      </div>
    );
  } else if (!(data.plans || []).length) {
    body = (
      <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center' }}>
        <i className="bi bi-kanban" style={{ color: brand.charcoal, fontSize: '1.8rem' }} />
        <h6 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>No plans found</h6>
        <p className="mb-0" style={{ color: brand.charcoal, fontSize: '0.86rem', maxWidth: 560, margin: '0 auto' }}>
          Microsoft returned no Planner boards for {hubEmail}.
          {data.rosters_enabled === false && (
            <> Boards that aren&rsquo;t attached to a Team are currently excluded — a Super Admin can
            include them in Configuration, which is usually what&rsquo;s missing here.</>
          )}
          {' '}Planner Premium plans (timelines, sprints, custom fields) are a different product and
          never appear here.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Tile icon="kanban-fill" label="Plans" value={totals.plans || 0} />
          <Tile icon="list-task" label="Open tasks" value={totals.open || 0} />
          <Tile icon="exclamation-triangle-fill" label="Overdue" value={totals.overdue || 0}
            tint={totals.overdue ? brand.red : undefined} />
          <Tile icon="calendar-week-fill" label="Due this week" value={totals.due_soon || 0}
            tint={totals.due_soon ? brand.amber : undefined} />
          <Tile icon="person-dash-fill" label="Unassigned" value={totals.unassigned || 0} />
          <Tile icon="check-circle-fill" label={`Completed in ${activityDays}d`}
            value={totals.completed_recent || 0} tint={totals.completed_recent ? brand.green : undefined}
            hint="Planner reports completions and new tasks only — edits and reassignments are not visible through its API." />
        </div>

        {data.ai_error && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.84rem' }}>{data.ai_error}</Alert>
        )}

        {(data.summary?.headline || (data.summary?.watch_items || []).length > 0) && (
          <div style={{ ...cardStyle, padding: '1rem', borderLeft: `4px solid ${brand.goldenrod}` }} className="mb-3">
            {data.summary.headline && (
              <p className="mb-0" style={{ ...WRAP, color: brand.deepBlue, fontSize: '1rem', fontWeight: 600 }}>
                {data.summary.headline}
              </p>
            )}
            {!!(data.summary.watch_items || []).length && (
              <ul className="mb-0 mt-2 ps-3" style={{ fontSize: '0.86rem', color: brand.charcoal }}>
                {data.summary.watch_items.map((w, i) => <li key={i} style={WRAP}>{w}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="d-flex flex-column gap-3">
          {data.plans.map((p) => (
            <PlanCard key={p.plan_id} plan={p} note={notes[p.plan_id]} activityDays={activityDays} />
          ))}
        </div>

        <div className="mt-3" style={{ fontSize: '0.76rem', color: brand.charcoal }}>
          <p className="mb-1">
            <i className="bi bi-info-circle me-1" />
            Recent activity means tasks <strong>created or completed</strong> in the last {activityDays} days.
            Planner&rsquo;s API publishes no &ldquo;last modified&rdquo; date, so edits, reassignments,
            bucket moves and due-date changes cannot be shown here.
          </p>
          {!!totals.plans_omitted && (
            <p className="mb-1">
              Showing {totals.plans} of {totals.plans_available} plans you belong to
              ({totals.plans_omitted} not read — the per-request ceiling keeps Planner from throttling
              the page; a Super Admin can raise it in Configuration).
            </p>
          )}
          {!!(data.unreadable || []).length && (
            <p className="mb-1">
              {data.unreadable.length} plan(s) could not be read and are not counted above:{' '}
              {data.unreadable.map((u) => u.title).filter(Boolean).join(', ')}.
            </p>
          )}
          {!!totals.plans_assigned && (
            <p className="mb-1">
              {totals.plans_assigned} {totals.plans_assigned === 1 ? 'board is' : 'boards are'} included
              because you have tasks on {totals.plans_assigned === 1 ? 'it' : 'them'}, not because you
              own {totals.plans_assigned === 1 ? 'it' : 'them'}.
              {!!totals.plans_mine_only && ` For ${totals.plans_mine_only} of those, only your own tasks are readable — those cards are labelled and their figures are not board totals.`}
            </p>
          )}
          {!!totals.plans_roster && (
            <p className="mb-1">
              {totals.plans_roster} of these {totals.plans_roster === 1 ? 'is a board' : 'are boards'} shared
              with you directly rather than through a Team. Microsoft lists those through a preview API,
              so if one goes missing here it is still safe in Planner.
            </p>
          )}
          {data.ai_enabled === false && (
            <p className="mb-0">The AI summary is switched off; the figures above are unaffected.</p>
          )}
        </div>
      </>
    );
  }

  return (
    <Container fluid className="py-3 px-md-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h4 className="mb-0" style={{ color: brand.deepBlue, fontWeight: 800 }}>
            Planner
            <Badge bg="" className="ms-2" style={{ background: brand.goldenrod, color: brand.deepBlue, fontSize: '0.6rem', verticalAlign: 'middle' }}>
              BETA
            </Badge>
          </h4>
          <div style={{ color: brand.charcoal, fontSize: '0.84rem' }}>
            Where your project boards stand, summarized.
          </div>
        </div>
        {phase !== 'connect' && (
          <button type="button" onClick={refresh} disabled={refreshing || res.isFetching}
            style={btn(brand.softGray, brand.deepBlue)}>
            <i className="bi bi-arrow-clockwise me-1" />
            {refreshing || res.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>
      {body}
    </Container>
  );
}
